
import {
    FileText,
    ArrowLeft,
    Save,
    Printer,
    Mail,
    BookOpen,
    FilePlus,
    History,
    Trash2,
    LayoutList,
    LayoutGrid,
    ClipboardList,
    Search,
    Check,
} from "lucide-react";
import { useLanguage } from "@/context/LanguageContext";
import {
    Command,
    CommandInput,
    CommandList,
    CommandEmpty,
    CommandGroup,
    CommandItem,
} from "@/components/ui/command";

import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@radix-ui/react-popover";
import { Button } from "@/components/ui/button";
import { useDispatch, useSelector } from "react-redux";
import CelebrationButton from "@/components/ui/CelebrationButton/CelebrationButton";

const InputField = ({ label, value, required, onChange, ...rest }) => (
    <div className="space-y-1 w-full">
        {label && (
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
                {label} {required && <span className="text-red-700">*</span>}
            </label>
        )}
        <input
            value={value || ""}
            onChange={onChange}
            {...rest}
            className="w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs outline-none focus:ring-2 focus:ring-teal-500"
        />
    </div>
);

import Select from "react-select";
import {
    BillTypeOptions,
    divisionOptions,
    officeTypeOptions,
    paymentTermsOptions,
    PROFIT_CENTERS,
    statusOptions,
    typeOptions,
} from "./getInvoiceColumns";
import DatePicker from "react-datepicker";
import moment from "moment";
import ChargesSection from "./ChargesSection";
import MessageNotes from "./MessageNotes";
import { SearchBar } from "@/components/SearchBox/SearchBar";
import { getBookingList } from "@/redux/Booking/actions";
import { useDebounced } from "@/hooks/useDebounced";

const SelectField = ({
    label,
    options,
    value,
    onChange = () => { },
    select = { label: "label", value: "value" },
    required,
}) => {
    // convert string array → react-select format
    const formattedOptions = options.map((opt) => ({
        label:
            typeof opt === "string" || typeof opt === "number"
                ? opt
                : opt?.[select?.label],
        value:
            typeof opt === "string" || typeof opt === "number"
                ? opt
                : opt?.[select?.value],
    }));

    return (
        <div className="space-y-1 w-full">
            <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
                {label} {required && <span className="text-red-700">*</span>}
            </label>

            <Select
                options={formattedOptions}
                value={formattedOptions.find((o) => o.value === value)}
                onChange={(selected) => onChange(selected?.value)}
                className="text-xs"
                classNamePrefix="react-select"
                isSearchable
            />
        </div>
    );
};

const DateField = ({ label, value, color, onChange = () => { }, required }) => (
    <div className={`w-full ${color ? "p-2 rounded-lg border " + color : ""}`}>
        <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
            {label} {required && <span className="text-red-700">*</span>}
        </label>
        <DatePicker
            selected={value ? new Date(value) : null}
            showIcon
            onChange={(date) => onChange(moment(date).format("YYYY-MM-DD"))}
            dateFormat="dd MMM yyyy"
            placeholderText="Select Date"
            className="w-full  px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs outline-none focus:ring-2 focus:ring-teal-500"
        />
    </div>
);

// --- MAIN COMPONENT ---
const EditInvoiceModel = ({
    editingInvoice,
    onClose,
    formData,
    setFormData = { setFormData },
    changeHandler,
    handleSubmit,
    onDelete = () => { },
    setShowLinkChargesDialog,
    setMetadata,
    metadata,
    handlePrint,
    handleEmail,
    handleTechGuide,
    handleInvoiceLog,
    handleNewInvoice,
    loading = false,
    type = "INVOICE",
    fetchBook = () => { }
}) => {
    const { t } = useLanguage();
    const isEditing = !!editingInvoice;
    const { isAnimate } = useSelector((state) => state.toast);

    const [view, setView] = useState("");
    const [activeTab, setActiveTab] = useState("details");
    const [search, setSearch] = useState("");
    const [open, setOpen] = useState(false);
    const dispatch = useDispatch();
    const handleMetaChange = (key, value) => {
        setMetadata((pre) => ({ ...pre, [key]: value }));
        console.log("Metadata updated:", { ...metadata, [key]: value });
    };

    const actionButtons = [
        {
            label: "Save",
            icon: Save,
            onClick: handleSubmit,
            gradient:
                "from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700",
        },
        ...(isEditing
            ? [
                {
                    label: "Print",
                    icon: Printer,
                    onClick: handlePrint,
                    gradient:
                        "from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800",
                },
                {
                    label: "Email",
                    icon: Mail,
                    onClick: handleEmail,
                    gradient:
                        "from-teal-600 to-cyan-600 hover:from-teal-700 hover:to-cyan-700",
                },
            ]
            : []),

        {
            label: "Tech Guide",
            icon: BookOpen,
            onClick: handleTechGuide,
            gradient:
                "from-indigo-600 to-violet-600 hover:from-indigo-700 hover:to-violet-700",
        },
        {
            label: "Add New",
            icon: FilePlus,
            onClick: handleNewInvoice,
            gradient:
                "from-emerald-500 to-green-600 hover:from-emerald-600 hover:to-green-700",
        },
        {
            label: "Invoice Log",
            icon: History,
            onClick: handleInvoiceLog,
            gradient:
                "from-slate-600 to-gray-700 hover:from-slate-700 hover:to-gray-800",
        },
        ...(isEditing
            ? [
                {
                    label: "Delete",
                    icon: Trash2,
                    onClick: onDelete,
                    gradient:
                        "from-red-600 to-red-700 hover:from-red-700 hover:to-red-800",
                },
            ]
            : []),
    ];
    const labelCls = "text-xs font-medium text-muted-foreground";

    const { bookingList } = useSelector(
        (state) => state.booking
    );

    const fetchBookings = async () => {

        try {
            const payload = {
                IPageNo: 1,
                IPageRecords: 50,
                FilterType: "ALL",
                SearchKeyword: search || "",
                CARRIER: "",
                pol: "",
                pod: "",
                shortby: "",
                FromDate: "",
                ToDate: ""
            };
            await dispatch(getBookingList(payload));
        } catch (error) {

        } finally {

        }
    };

    const debouncedSearch = useDebounced((param) => {
        fetchBookings(param)
    }, 300);
    useEffect(() => {
        if (search === null) return;
        debouncedSearch(search);
    }, [search]);

    const isBill = type === "BILL";

    //     useEffect(()=>{
    // fetchBookings()
    //     },[])

    return (
        <div className="fixed inset-0 top-0 mt-0 z-50 w-full flex items-center justify-center">
            {/* Overlay */}
            <div className="absolute inset-0 bg-black/60" onClick={onClose} />

            <div
                className={`
              relative z-10 grid gap-4 border shadow-lg
              bg-white dark:bg-slate-950
              w-[1400px] max-w-[95vw]
              max-h-[95vh] overflow-y-auto
              p-0 sm:rounded-lg
              w-full

              transform transition-all duration-200

            `}
            >
                <div className="max-w-[1600px] mx-auto shadow-2xl rounded-lg overflow-hidden bg-white dark:bg-slate-950 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-800 w-full">
                    {/* --- HEADER --- */}
                    <div className="px-6 pt-5 pb-4 border-b border-slate-200 dark:border-slate-800 bg-gradient-to-r from-[#1a365d] via-[#1a365d]/90 to-[#0d9488]">
                        <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                            <h2 className="font-semibold tracking-tight flex items-center gap-3 text-lg text-white">
                                <div className="p-2 rounded-lg bg-white/10">
                                    <FileText className="h-5 w-5 text-white" />
                                </div>

                                {type !== "BILL" ? (
                                    <div className="flex items-center">
                                        <span>
                                            {" "}
                                            {editingInvoice ? `${t("editInvoice")}` : t("newInvoice")}
                                        </span>
                                        {editingInvoice?.invoice_no && (
                                            <span className="ml-3 px-2.5 py-0.5 rounded-full text-xs bg-white/20">
                                                {editingInvoice?.invoice_no}
                                            </span>
                                        )}
                                    </div>
                                ) : (
                                    <div className="flex items-center">
                                        <span>
                                            {" "}
                                            {editingInvoice
                                                ? `Update Vendor Bill`
                                                : "Create Vendor Bill"}
                                        </span>
                                        {editingInvoice?.bill_no && (
                                            <span className="ml-3 px-2.5 py-0.5 rounded-full text-xs bg-white/20">
                                                {editingInvoice?.bill_no}
                                            </span>
                                        )}
                                    </div>
                                )}
                            </h2>

                            <button
                                onClick={onClose}
                                className="inline-flex items-center h-7 px-3 text-[11px] text-white hover:bg-white/20 rounded-full gap-1"
                            >
                                <ArrowLeft className="h-3.5 w-3.5" />
                                Back
                            </button>
                        </div>

                        {/* Action Toolbar */}
                        <div className="flex flex-wrap items-center gap-1.5 mt-3 pt-3 border-t border-white/20">
                            {actionButtons.map((btn) => {
                                const Icon = btn.icon;
                                if (btn.label === "Save") {
                                    return (
                                        <CelebrationButton
                                            onClick={btn.disabled ? () => { } : btn.onClick}
                                            label={
                                                btn.label === "Save"
                                                    ? loading
                                                        ? "Saving..."
                                                        : isEditing
                                                            ? type === "BILL"
                                                                ? "Update Bill"
                                                                : "Update Invoice"
                                                            : type === "BILL"
                                                                ? "Save Bill"
                                                                : "Save Invoice"
                                                    : btn.label
                                            }
                                            className={cn(
                                                "h-7 px-3 text-[11px] font-medium text-white border-0 rounded-full shadow-sm gap-1.5",
                                                `bg-gradient-to-r ${btn.gradient}`,
                                            )}
                                            icon={<Icon className="h-3.5 w-3.5" />}
                                            isSuccess={isAnimate}
                                        />
                                    );
                                }
                                return (
                                    <Button
                                        key={btn.label}
                                        type="button"
                                        size="sm"
                                        disabled={btn.disabled}
                                        onClick={btn.disabled ? () => { } : btn.onClick}
                                        className={cn(
                                            "h-7 px-3 text-[11px] font-medium text-white border-0 rounded-full shadow-sm gap-1.5",
                                            `bg-gradient-to-r ${btn.gradient}`,
                                        )}
                                    >
                                        <Icon className="h-3.5 w-3.5" />
                                        {btn.label}
                                    </Button>
                                );
                            })}
                            {/* View Mode Toggle */}
                            <div className="ml-auto flex items-center gap-1 bg-white/10 rounded-full p-0.5">
                                <button
                                    type="button"
                                    onClick={() => setView("")}
                                    className={cn(
                                        "p-1.5 rounded-full transition-colors",
                                        view === ""
                                            ? "bg-white/25 text-white"
                                            : "text-white/60 hover:text-white",
                                    )}
                                    title="Scroll View"
                                >
                                    <LayoutList className="h-3.5 w-3.5" />
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setView("tab")}
                                    className={cn(
                                        "p-1.5 rounded-full transition-colors",
                                        view === "tab"
                                            ? "bg-white/25 text-white"
                                            : "text-white/60 hover:text-white",
                                    )}
                                    title="Tab View"
                                >
                                    <LayoutGrid className="h-3.5 w-3.5" />
                                </button>
                            </div>
                        </div>
                    </div>

                    {view === "tab" && (
                        <Tabs activeTab={activeTab} setActiveTab={setActiveTab} />
                    )}
                    {/* --- FORM --- */}
                    <div className="p-4">
                        {(!view || activeTab === "details") && (
                            <div className="rounded-lg border border-slate-200 dark:border-slate-800 border-l-4 border-l-teal-500 ">
                                {/* Sub-header */}
                                <div className="flex items-center gap-3 px-4 py-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900">
                                    <div className="p-2 rounded-lg bg-teal-600">
                                        <ClipboardList className="h-4 w-4 text-white" />
                                    </div>
                                    <span className="text-sm font-semibold uppercase">
                                        {type === "BILL" ? "Bill Information" : "Invoice Details"}
                                    </span>
                                </div>

                                <div className="px-5 py-6 grid grid-cols-1 md:grid-cols-2 gap-8">
                                    {/* LEFT */}
                                    <div className="space-y-4">
                                        <div className="p-3 rounded-lg border bg-blue-50 dark:bg-blue-900/10 border-blue-200 dark:border-blue-800">
                                            {type !== "BILL" ? (
                                                <div className="space-y-1 w-full">
                                                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                        Customer Name{" "}
                                                        <span className="text-red-700">*</span>
                                                    </label>
                                                    <SearchBar
                                                        searchAPI="v1/AutoSuggestion/GetCustomerSuggestion?Prefix="
                                                        selectReacord={(d) => {
                                                            changeHandler("customer_name", d?.[1]);
                                                        }}
                                                        value={formData?.customer_name || ""}
                                                        onchange={(val) => {
                                                            if (!val) {
                                                                changeHandler("customer_name", "");
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            ) : (
                                                <div className="space-y-1 w-full">
                                                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                        Vendor <span className="text-red-700">*</span>
                                                    </label>
                                                    <SearchBar
                                                        searchAPI="v1/AutoSuggestion/GetVendorList?Prefix="
                                                        selectReacord={(d) => {
                                                            changeHandler("vendor_name", d?.[1]);
                                                            changeHandler("vendor_id", d?.[0]);
                                                        }}
                                                        value={formData?.vendor_name || ""}
                                                        onchange={(val) => {
                                                            if (!val) {
                                                                changeHandler("vendor_name", "");
                                                            }
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <InputField
                                                label="File Reference Number"
                                                placeholder="Enter file reference"
                                                required
                                                value={metadata?.file_reference_no}
                                                onChange={(e) =>
                                                    handleMetaChange("file_reference_no", e.target.value)
                                                }
                                            />
                                            <InputField
                                                label="Customer PO Number"
                                                placeholder="PO#"
                                                value={metadata?.customer_po_number}
                                                onChange={(e) =>
                                                    handleMetaChange("customer_po_number", e.target.value)
                                                }
                                            />
                                        </div>

                                        <div>
                                            <label className={labelCls}>Booking Number</label>
                                            <div className="flex gap-1">
                                                <input
                                                    className={cn(
                                                        "w-full h-10 px-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs outline-none focus:ring-2 focus:ring-teal-500",
                                                        "flex-1",
                                                    )}
                                                    value={metadata?.BookingNumber}
                                                    onChange={(e) =>
                                                        setMetadata({
                                                            ...metadata,
                                                            BookingNumber: e.target.value,
                                                        })
                                                    }
                                                    placeholder="Enter or search booking"
                                                />
                                                <Popover open={open} onOpenChange={setOpen}>
                                                    <PopoverTrigger asChild>
                                                        <Button
                                                            type="button"
                                                            variant="outline"
                                                            size="sm"
                                                            className="h-10 w-10 p-0 shrink-0"
                                                        >
                                                            <Search className="h-3.5 w-3.5" />
                                                        </Button>
                                                    </PopoverTrigger>

                                                    <PopoverContent className="w-[280px] p-0" align="end">
                                                        <Command shouldFilter={false}> {/* ✅ disable local filtering */}

                                                            <CommandInput
                                                                placeholder="Search bookings..."
                                                                value={search}
                                                                onValueChange={(val) => setSearch(val)}
                                                            />

                                                            <CommandList>
                                                                <CommandEmpty>No bookings found.</CommandEmpty>

                                                                <CommandGroup>
                                                                    {(bookingList || []).map((b) => (
                                                                        <CommandItem
                                                                            key={b.id}
                                                                            value={b?.fileNumber} // now safe, won't filter
                                                                            onSelect={() => {
                                                                                fetchBook(b?.id);
                                                                                setOpen(false);
                                                                                setSearch("");
                                                                            }}
                                                                        >
                                                                            {b?.carrierBookingNumber}
                                                                        </CommandItem>
                                                                    ))}
                                                                </CommandGroup>
                                                            </CommandList>
                                                        </Command>
                                                    </PopoverContent>
                                                </Popover>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <InputField
                                                label="Origin"
                                                value={metadata?.origin_name}
                                                placeholder="Origin port"
                                                onChange={(e) =>
                                                    handleMetaChange("origin_name", e.target.value)
                                                }
                                            />
                                            <InputField
                                                label="Origin Code"
                                                placeholder="Code"
                                                value={metadata?.origin_code}
                                                onChange={(e) =>
                                                    handleMetaChange("origin_code", e.target.value)
                                                }
                                            />
                                        </div>
                                        <div className="grid grid-cols-2 gap-4">
                                            <InputField
                                                label="Destination"
                                                placeholder="Destination port"
                                                value={metadata?.destination_name}
                                                onChange={(e) =>
                                                    handleMetaChange("destination_name", e.target.value)
                                                }
                                            />
                                            <InputField
                                                label="Destination Code"
                                                placeholder="Code"
                                                value={metadata?.destination_code}
                                                onChange={(e) =>
                                                    handleMetaChange("destination_code", e.target.value)
                                                }
                                            />
                                        </div>
                                        <div className="grid grid-cols-1 gap-4">
                                            <div className="space-y-1 w-full">
                                                {
                                                    <label className="text-xs font-medium text-slate-500 dark:text-slate-400">
                                                        Descriptions (Vessel/Voyage)
                                                    </label>
                                                }
                                                <textarea
                                                    value={metadata?.descriptions}
                                                    onChange={(e) =>
                                                        handleMetaChange("descriptions", e.target.value)
                                                    }
                                                    placeholder="Vessel name / Vessel code"
                                                    className="w-full h-20 p-3 rounded-md border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-900 text-xs outline-none focus:ring-2 focus:ring-teal-500"
                                                />
                                            </div>
                                        </div>
                                    </div>

                                    {/* RIGHT */}
                                    <div className="space-y-4">
                                        <div className="grid grid-cols-2 gap-4">
                                            <SelectField
                                                label="Division"
                                                options={divisionOptions}
                                                required
                                                value={metadata?.division}
                                                onChange={(value) =>
                                                    handleMetaChange("division", value)
                                                }
                                            />
                                            <SelectField
                                                label="Type"
                                                options={
                                                    type === "BILL" ? BillTypeOptions : typeOptions
                                                }
                                                value={formData?.invoice_type}
                                                onChange={(value) =>
                                                    handleMetaChange("invoice_type", value)
                                                }
                                                required
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <div className="p-2 border rounded bg-indigo-50 dark:bg-indigo-900/10">
                                                <InputField
                                                    label={isBill ? "Bill Number" : "Invoice Number"}
                                                    value={
                                                        (isBill ? editingInvoice?.bill_no : editingInvoice?.invoice_no)?.trim() ||
                                                        "Auto-generated"
                                                    }
                                                    placeholder={isBill ? "Bill Number" : "Invoice Number"}
                                                    disabled
                                                />
                                            </div>

                                            <div className="p-2 border rounded bg-amber-50 dark:bg-amber-900/10 flex justify-between items-center text-xs w-full">
                                                <SelectField
                                                    label="status"
                                                    options={statusOptions}
                                                    value={formData?.status}
                                                    required
                                                    onChange={(value) => changeHandler("status", value)}
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <DateField
                                                label={type === "BILL" ? "Bill Date" : "Invoice Date"}
                                                value={isBill ? formData?.bill_date : formData?.invoice_date}
                                                onChange={(e) =>
                                                    isBill
                                                        ? changeHandler("bill_date", e)
                                                        : changeHandler("invoice_date", e)
                                                }
                                                color="bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800"
                                            />
                                            <DateField
                                                label="Due Date"
                                                value={formData.due_date}
                                                onChange={(e) => changeHandler("due_date", e)}
                                                color="bg-rose-50 dark:bg-rose-900/10 border-rose-200 dark:border-rose-800"
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <SelectField
                                                label="Terms"
                                                value={formData.terms}
                                                options={paymentTermsOptions}
                                                onChange={(value) => changeHandler("terms", value)}
                                                required
                                            />
                                            <SelectField
                                                label="Office Type"
                                                options={officeTypeOptions}
                                                required
                                                value={metadata?.office_type}
                                                onChange={(value) =>
                                                    handleMetaChange("office_type", value)
                                                }
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <DateField
                                                label="On Board Date"
                                                value={metadata?.on_board_date}
                                                onChange={(e) => handleMetaChange("on_board_date", e)}
                                            />
                                            <DateField
                                                label="ETA Date"
                                                value={metadata?.eta_date}
                                                onChange={(e) => handleMetaChange("eta_date", e)}
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <InputField
                                                label="Weight (KGS)"
                                                placeholder="0.00"
                                                type="number"
                                                min={0}
                                                value={metadata?.weight_kgs}
                                                onChange={(e) =>
                                                    handleMetaChange("weight_kgs", e.target.value)
                                                }
                                            />
                                            <InputField
                                                label="Measurement (CBM)"
                                                placeholder="0.00"
                                                type="number"
                                                min={0}
                                                value={metadata?.measurement_cbm}
                                                onChange={(e) =>
                                                    handleMetaChange("measurement_cbm", e.target.value)
                                                }
                                            />
                                        </div>

                                        <div className="grid grid-cols-2 gap-4">
                                            <SelectField
                                                label="Profit Center"
                                                options={PROFIT_CENTERS}
                                                required
                                                value={metadata?.profit_center}
                                                onChange={(e) => handleMetaChange("profit_center", e)}
                                            />
                                            <InputField
                                                label="Email"
                                                placeholder="customer name email"
                                                value={metadata?.email}
                                                onChange={(e) =>
                                                    handleMetaChange("email", e.target.value)
                                                }
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                        {(!view || activeTab === "charges") && (
                            <ChargesSection
                                formData={formData}
                                setFormData={setFormData}
                                setMetadata={setMetadata}
                                metadata={metadata}
                                setShowLinkChargesDialog={setShowLinkChargesDialog}
                            />
                        )}
                        {(!view || activeTab === "notes") && (
                            <MessageNotes
                                formData={formData}
                                setFormData={setFormData}
                                setMetadata={setMetadata}
                                metadata={metadata}
                            />
                        )}
                        <div className="px-6 py-4 mt-2 mb-2 flex flex-wrap justify-center gap-2">
                            {actionButtons.map((btn) => {
                                const Icon = btn.icon;
                                if (btn.label === "Save") {
                                    return (
                                        <CelebrationButton
                                            onClick={btn.disabled ? () => { } : btn.onClick}
                                            label={
                                                btn.label === "Save"
                                                    ? loading
                                                        ? "Saving..."
                                                        : isEditing
                                                            ? type === "BILL"
                                                                ? "Update Bill"
                                                                : "Update Invoice"
                                                            : type === "BILL"
                                                                ? "Save Bill"
                                                                : "Save Invoice"
                                                    : btn.label
                                            }
                                            className={cn(
                                                "h-9 px-5 text-xs font-semibold text-white border-0 rounded-full shadow-md gap-2",
                                                `bg-gradient-to-r ${btn.gradient}`,
                                            )}
                                            icon={<Icon className="h-4 w-4" />}
                                            isSuccess={isAnimate}
                                        />
                                    );
                                }
                                return (
                                    <Button
                                        key={btn.label}
                                        type={btn.label === "Save" ? "submit" : "button"}
                                        size="sm"
                                        disabled={btn.disabled}
                                        onClick={btn.disabled ? () => { } : btn.onClick}
                                        className={cn(
                                            "h-9 px-5 text-xs font-semibold text-white border-0 rounded-full shadow-md gap-2",
                                            `bg-gradient-to-r ${btn.gradient}`,
                                        )}
                                    >
                                        <Icon className="h-4 w-4" />
                                        {btn.label === "Save"
                                            ? loading
                                                ? "Saving..."
                                                : isEditing
                                                    ? type === "BILL"
                                                        ? "Update Bill"
                                                        : "Update Invoice"
                                                    : type === "BILL"
                                                        ? "Save Bill"
                                                        : "Save Invoice"
                                            : btn.label}
                                    </Button>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default EditInvoiceModel;



const Tabs = ({ activeTab, setActiveTab }) => {
    const tabs = [
        { key: "details", label: "Details", color: "bg-teal-500" },
        { key: "charges", label: "Charges", color: "bg-amber-500" },
        { key: "notes", label: "Notes", color: "bg-purple-500" },
    ];

    return (
        <div className="flex items-center gap-1 px-4 pt-3 pb-1">
            {tabs.map((tab) => {
                const isActive = activeTab === tab.key;

                return (
                    <button
                        key={tab.key}
                        onClick={() => setActiveTab(tab.key)}
                        className={`flex items-center gap-1.5 px-4 py-2 rounded-lg text-xs font-medium transition-all
              ${isActive
                                ? "bg-muted shadow-sm text-foreground"
                                : "text-muted-foreground hover:bg-muted/50"
                            }`}
                    >
                        <span className={`h-2 w-2 rounded-full ${tab.color}`} />
                        {tab.label}
                    </button>
                );
            })}
        </div>
    );
};

Tabs;
